import { cookieSink, localSink, type StorageAdapter, sessionSink } from '@bomdisoft/store/persisted/storage';
import type { PersistedHandle, PersistedSpec, Sink } from '@bomdisoft/store/persisted/types';
import { queueParam, readParam, subscribeToUrl } from '@bomdisoft/store/persisted/url';

const ADAPTERS: Record<Exclude<Sink, 'url'>, StorageAdapter> = { cookie: cookieSink, local: localSink, session: sessionSink };

const ORDER: readonly Sink[] = ['url', 'session', 'local', 'cookie'];

export const persisted = <T>(spec: PersistedSpec<T>): PersistedHandle<T> => {
  const param = spec.param ?? spec.key;
  const defaultRaw = spec.codec.format(spec.default);
  const carriesUrl = spec.in.includes('url');

  const listeners = new Map<string, Set<() => void>>();
  const cache = new Map<string, { source: Sink | 'default'; urlRaw: string | undefined; value: T }>();
  const urlCache = new Map<string, T>();
  const detachers: (() => void)[] = [];

  const slot = (scope?: string) => scope ?? '';
  const urlNow = () => (carriesUrl ? readParam(param) : undefined);
  const nameFor = (scope?: string) => (scope === undefined ? spec.key : `${spec.key}:${scope}`);
  const wrap = (raw: string) => (spec.version === undefined ? raw : JSON.stringify({ v: spec.version, value: raw }));

  const fromStored = (raw: string): T | undefined => {
    if (spec.version === undefined) return spec.codec.parse(raw);
    let from = 0;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const envelope = parsed as { v?: unknown; value?: unknown };
        if (typeof envelope.v === 'number') {
          if (envelope.v === spec.version && typeof envelope.value === 'string') return spec.codec.parse(envelope.value);
          from = envelope.v;
        }
      }
    } catch {
      from = 0;
    }
    return spec.migrate?.(raw, from);
  };

  const resolve = (scope?: string): { source: Sink | 'default'; value: T } => {
    const name = nameFor(scope);
    for (const sink of ORDER) {
      if (!spec.in.includes(sink)) continue;
      if (sink === 'url') {
        const raw = readParam(param);
        if (raw === undefined) continue;
        const value = spec.codec.parse(raw);
        if (value !== undefined) return { source: 'url', value };
        continue;
      }
      const adapter = ADAPTERS[sink];
      const raw = adapter.read(name);
      if (raw === undefined) continue;
      const value = fromStored(raw);
      if (value !== undefined) return { source: sink, value };
      adapter.remove(name);
    }
    return { source: 'default', value: spec.default };
  };

  /**
   * One entry per scope, replaced rather than added to when the address moves.
   *
   * The cache is what lets `useSyncExternalStore` see a stable reference between
   * renders, which an object-valued codec needs or every render is a new object.
   * Keying it by the address instead would leave an entry per address visited,
   * and an address returning to an earlier value would revive a value the sinks
   * no longer hold.
   */
  const current = (scope?: string) => {
    const key = slot(scope);
    const raw = urlNow();
    const cached = cache.get(key);
    if (cached !== undefined && cached.urlRaw === raw) return cached;
    const resolved = { ...resolve(scope), urlRaw: raw };
    cache.set(key, resolved);
    return resolved;
  };

  const notify = (scope?: string) => {
    for (const listener of listeners.get(slot(scope)) ?? []) listener();
  };

  const invalidateAll = () => {
    cache.clear();
    for (const set of listeners.values()) for (const listener of set) listener();
  };

  const attachSources = () => {
    if (detachers.length > 0) return;
    if (carriesUrl) detachers.push(subscribeToUrl(invalidateAll));
    if (spec.in.includes('local')) detachers.push(localSink.subscribe(spec.key, invalidateAll));
    if (spec.in.includes('session')) detachers.push(sessionSink.subscribe(spec.key, invalidateAll));
  };

  return {
    fromUrl: (raw, scope) => {
      if (raw === null || !carriesUrl) return spec.default;
      const key = `${slot(scope)} ${raw}`;
      if (urlCache.has(key)) return urlCache.get(key) as T;
      const value = spec.codec.parse(raw) ?? spec.default;
      urlCache.set(key, value);
      return value;
    },
    key: spec.key,
    param,
    read: scope => current(scope).value,
    /* Reflects the value the CALLER is showing, not one re-read here. A hook
       reflects what it rendered, so the address cannot come to disagree with
       the screen it is supposed to describe. */
    reflect: value => {
      if (!carriesUrl) return;
      const inUrl = readParam(param);
      if (inUrl !== undefined && spec.codec.parse(inUrl) === undefined) {
        queueParam(param, null);
        return;
      }
      const raw = spec.codec.format(value);
      if (raw === defaultRaw || inUrl === raw) return;
      queueParam(param, raw);
    },
    refresh: scope => {
      cache.delete(slot(scope));
      notify(scope);
    },
    reset: scope => {
      const name = nameFor(scope);
      for (const sink of spec.in) {
        if (sink === 'url') queueParam(param, null);
        else ADAPTERS[sink].remove(name);
      }
      cache.set(slot(scope), { source: 'default', urlRaw: undefined, value: spec.default });
      notify(scope);
    },
    source: scope => current(scope).source,
    spec,
    subscribe: (listener, scope) => {
      attachSources();
      const key = slot(scope);
      const set = listeners.get(key) ?? new Set<() => void>();
      listeners.set(key, set);
      set.add(listener);
      return () => {
        set.delete(listener);
      };
    },
    write: (next, scope) => {
      const value = typeof next === 'function' ? (next as (value: T) => T)(current(scope).value) : next;
      const raw = spec.codec.format(value);
      const name = nameFor(scope);
      for (const sink of spec.in) {
        if (sink === 'url') queueParam(param, raw === defaultRaw ? null : raw);
        else ADAPTERS[sink].write(name, wrap(raw));
      }
      /* Cached against the address this write is ON ITS WAY to producing, not
         the one still showing: the URL write is queued to a microtask, so
         caching the current address would make the entry go stale the moment it
         flushes and hand the next read a fresh object for an unchanged value. */
      cache.set(slot(scope), { source: spec.in[0] ?? 'default', urlRaw: carriesUrl && raw !== defaultRaw ? raw : undefined, value });
      notify(scope);
    },
  };
};
